import { PinIcon } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiOutlineViewGrid } from "react-icons/hi";
import { HiListBullet } from "react-icons/hi2";
import { useDispatch, useSelector } from "react-redux";
import { FilterSelect, SingleSelect } from "../../../components/common/Search";
import SortByDropdown from "../../../components/common/SortByDropdown";
import Portal from "../../../components/Portal";
import { workUnitsTableHeaders } from "../../../constants/constants";
import {
  getStatusOptions
} from "../../../constants/integrationTypes";
import SearchWithFilter from "../../../features/WorkUnits/components/SearchWithFilter/SearchWithFilter";
import { useJwt } from "../../../helpers/common";
import { useIntegrationStatus } from "../../../hooks/useIntegrationStatus";
import {
  clearWorkItemsData,
  setWorkItemsSearchTerm,
} from "../../../redux/slices/organization";
import { useOrganizationService } from "../../../services";
import TooltipWrapper from "../../../shared/atoms/TooltipWrapper";

type SearchMode = "project" | "roadmap";
type SortBy =
  | "name_asc"
  | "name_desc"
  | "created_at_asc"
  | "created_at_desc"
  | "modified_at_asc"
  | "modified_at_desc";

interface WorkUnitsFilterProps {
  visibleColumns: Record<string, boolean>;
  setVisibleColumns: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  viewType: "list" | "grid";
  setViewType: (type: "list" | "grid") => void;
  projectData?: any[];
  onFilteredDataChange?: (filteredData: any[]) => void;
  totalItems?: number;
  currentPage?: number;
  totalPages?: number;
  loading?: boolean;
  tableScrollRef?: React.RefObject<HTMLDivElement>;
  gridScrollRef?: React.RefObject<HTMLDivElement>;

  isCompact?: boolean;
  searchType: "all" | "roadmap" | "project" | "epic" | "feature";
  setSearchType: (type: "all" | "roadmap" | "project" | "epic" | "feature") => void;
  sortBy: SortBy;
  setSortBy: (sort: SortBy) => void;
  sourceFilter: string;
  setSourceFilter: (source: string) => void;
  statusFilter: string[];
  setStatusFilter: (status: string[]) => void;
  pinnedFilter: boolean;
  setPinnedFilter: (pinned: boolean) => void;
  quarterFilter: string[];
  setQuarterFilter: (quarter: string[]) => void;
  loadingMore?: boolean;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
}

const WorkUnitsFilters: React.FC<WorkUnitsFilterProps> = ({
  visibleColumns,
  setVisibleColumns,
  viewType,
  setViewType,
  projectData = [],
  onFilteredDataChange,
  totalItems = 0,
  currentPage = 1,
  totalPages = 1,
  loading = false,
  tableScrollRef,
  gridScrollRef,
  isCompact = false,
  searchType,
  setSearchType,
  sortBy,
  setSortBy,
  sourceFilter,
  setSourceFilter,
  statusFilter,
  setStatusFilter,
  pinnedFilter,
  setPinnedFilter,
  quarterFilter,
  setQuarterFilter,
  loadingMore = false,
  hasActiveFilters = false,
  onClearFilters,
}) => {
  const dispatch = useDispatch();
  const jwtData: any = useJwt();
  const isAdmin =
    typeof (jwtData as any)?.org_role === "string" &&
    (jwtData as any).org_role?.toLowerCase() === "admin";
  const { fetchRoadmapsAndProjects } = useOrganizationService();
  const { isIntegrationActive } = useIntegrationStatus();

  // Get search term from Redux state - use workItemsSearchTerm for combined API
  const { workItemsSearchTerm: searchTerm, orgSkills } = useSelector(
    (state: any) => state.organization,
  );
  const { show_cost: showCost } = useSelector(
    (state: any) => state.organization.orgDetails || {},
  );

  // Search type and filters now managed by parent

  // Helper: compute effective stage filter based on status selection — REMOVED (stage filter removed)

  const handleStatusFilterChange = useCallback((selectedOptions: any) => {
    const newStatusFilter = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
    setStatusFilter(newStatusFilter);
    scrollToTop();
  }, [setStatusFilter]);



  const workItemTypeOptions = [
    { value: "task", label: "Task" },
    { value: "user_story", label: "User Story" },
    { value: "bug", label: "Bug" },
    { value: "spike", label: "Spike" },
  ];

  const skillOptions = useMemo(() => {
    return (orgSkills || []).map((s: any) => ({ value: s.id, label: s.skill }));
  }, [orgSkills]);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);
  const [resetKey, setResetKey] = useState<number>(0);

  // Refs for animated type indicator
  const typeButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>(
    {},
  );
  const [typeIndicatorStyle, setTypeIndicatorStyle] = useState({
    width: 0,
    left: 0,
  });

  // Source filter options - filtered based on active integrations
  const sourceFilterOptions = useMemo(() => {
    const baseOptions = [{ value: "", label: "All Sources" }];

    // Only include integration types that are active
    const integrationOptions = [
      { value: "azure", label: "Azure" },
      { value: "jira", label: "Jira" },
      { value: "asana", label: "Asana" },
      { value: "monday", label: "Monday" },
    ].filter((integration) => isIntegrationActive(integration.value));

    const extra = [
      {
        value: "cadence",
        label: "Cadence",
      },
    ];

    return [...baseOptions, ...integrationOptions, ...extra];
  }, [isIntegrationActive]);

  // Search type options for roadmap/project filter (used for pill buttons)
  const searchTypeOptions = useMemo(
    () => [
      { value: "all", label: "All Types" },
      { value: "epic", label: "Epics" },
      { value: "feature", label: "Features" },
    ],
    [],
  );

  // Status filter options
  const statusFilterOptions = getStatusOptions();

  // Filter projects based on status only (source filter is handled by API)
  const getFilteredProjects = useCallback(() => {
    let filtered = projectData;

    // Only filter by status locally (source filter is handled by API)
    if (statusFilter && statusFilter.length > 0) {
      filtered = filtered.filter((project: any) => {
        return statusFilter.includes(project.status);
      });
    }

    return filtered;
  }, [statusFilter, projectData]);

  // Apply filtering and notify parent component (only for status filter, source is handled by API)
  useEffect(() => {
    const filteredData = getFilteredProjects();
    if (onFilteredDataChange) {
      onFilteredDataChange(filteredData);
    }
  }, [statusFilter, projectData, onFilteredDataChange, getFilteredProjects]);

  // Helper functions to scroll to top based on view type
  const scrollToTop = () => {
    if (viewType === "list" && tableScrollRef?.current) {
      // Scroll table container to top
      tableScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    } else if (viewType === "grid" && gridScrollRef?.current) {
      // Scroll grid container to top
      gridScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Local input state for immediate feedback - synced from Redux on mount
  const [localSearchInput, setLocalSearchInput] = useState(searchTerm);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local input when Redux searchTerm changes externally (e.g., navigation back to page)
  useEffect(() => {
    setLocalSearchInput(searchTerm);
  }, [searchTerm]);

  // Debounced search handler - only fires API call after user stops typing
  const handleSearch = useCallback(
    (val: string) => {
      // Update local input immediately for responsive UI
      setLocalSearchInput(val);

      // Clear any pending debounce timer
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      // Debounce the API call (400ms)
      searchDebounceRef.current = setTimeout(async () => {
        const value = val || "";
        dispatch(setWorkItemsSearchTerm(value));
        // Scroll to top when search changes
        scrollToTop();
      }, 400);
    },
    [dispatch],
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleColumnToggle = useCallback(
    (column: string) => {
      const visibleCount = Object.values(visibleColumns).filter(Boolean).length;

      if (visibleCount === 1 && visibleColumns[column]) {
        setShowTooltip(column);
        setTimeout(() => setShowTooltip(null), 2000);
        return;
      }

      setVisibleColumns((prevVisibleColumns) => ({
        ...prevVisibleColumns,
        [column]: !prevVisibleColumns[column],
      }));
    },
    [visibleColumns, setVisibleColumns],
  );

  const updateDropdownPosition = useCallback(() => {
    if (columnDropdownRef.current) {
      const rect = columnDropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 192,
      });
    }
  }, []);

  const handleDropdownToggle = useCallback(() => {
    setShowColumnDropdown(!showColumnDropdown);
    if (!showColumnDropdown) {
      updateDropdownPosition();
    }
  }, [showColumnDropdown, updateDropdownPosition]);

  const handleSortChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newSortBy = e.target.value as SortBy;
      setSortBy(newSortBy);
      scrollToTop();
    },
    [setSortBy],
  );

  const handleSearchTypeClick = useCallback(
    (newSearchType: "all" | "epic" | "feature" | "roadmap" | "project") => {
      setSearchType(newSearchType);
      scrollToTop();
      // Clear quarter state when switching to Features
      if (newSearchType === 'feature' && (quarterFilter || []).length > 0) {
        setQuarterFilter([]);
      }
    },
    [setSearchType, quarterFilter, setQuarterFilter],
  );

  // Update type indicator position
  const updateTypeIndicator = useCallback(() => {
    const activeButton = searchType ? typeButtonRefs.current[searchType] : null;
    if (activeButton) {
      setTypeIndicatorStyle({
        width: activeButton.offsetWidth,
        left: activeButton.offsetLeft,
      });
    }
  }, [searchType]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      updateTypeIndicator();
    });
    return () => cancelAnimationFrame(frame);
  }, [searchType, updateTypeIndicator]);

  useEffect(() => {
    window.addEventListener("resize", updateTypeIndicator);
    return () => window.removeEventListener("resize", updateTypeIndicator);
  }, [updateTypeIndicator]);

  // Clear quarter selections when Azure integration is disabled
  useEffect(() => {
    if (!loading && !isIntegrationActive('azure') && (quarterFilter || []).length > 0) {
      setQuarterFilter([]);
      scrollToTop();
    }
  }, [loading, isIntegrationActive, quarterFilter, setQuarterFilter]);

  const handleSourceFilterChange = useCallback(
    (option: any) => {
      setSourceFilter(option.value);
      scrollToTop();
    },
    [setSourceFilter],
  );

  const handlePinnedFilterChange = useCallback(() => {
    setPinnedFilter(!pinnedFilter);
    scrollToTop();
  }, [pinnedFilter, setPinnedFilter]);

  const handleFilterChange = useCallback(
    (type: string, selectedValues: (string | number)[]) => {
      if (type === "quarter") {
        setQuarterFilter(selectedValues as string[]);
        scrollToTop();
      }
    },
    [setQuarterFilter],
  );

  // Generate quarter options: current quarter + next 3 quarters + previous 3 years
  const quarterOptions = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentQuarter = Math.floor(currentMonth / 3) + 1;

    const options: { value: string; label: string }[] = [];

    // Calculate start (current quarter + 3)
    let startYear = currentYear;
    let startQuarter = currentQuarter + 3;
    while (startQuarter > 4) {
      startQuarter -= 4;
      startYear += 1;
    }

    // End at 3 years before current year, Q1
    const endYear = currentYear - 3;
    const endQuarter = 1;

    let year = startYear;
    let q = startQuarter;

    // Loop backwards from start point to end point
    while (year > endYear || (year === endYear && q >= endQuarter)) {
      const shortYear = String(year).slice(2);
      options.push({
        value: `${year}Q${q}`,
        label: `${shortYear}Q${q}`,
      });

      q--;
      if (q < 1) {
        q = 4;
        year--;
      }
    }
    return options;
  }, []);

  const searchFilters = useMemo(
    () => {
      const filters: any[] = [];
      
      // Only show quarter filter for 'all' and 'roadmap' (epic) types when Azure integration is active
      if (searchType !== 'feature' && isIntegrationActive('azure')) {
        filters.push({
          id: "quarter",
          label: "Quarter",
          options: quarterOptions,
        });
      }

      return filters;
    },
    [quarterOptions, searchType, isIntegrationActive],
  );

  // Memoized static options
  const sortOptions = useMemo(
    () => [
      { value: "name_asc", label: "Name (A–Z)" },
      { value: "name_desc", label: "Name (Z–A)" },
      { value: "modified_at_desc", label: "Recently modified" },
      { value: "modified_at_asc", label: "Least recently modified" },
      { value: "created_at_desc", label: "Newest created" },
      { value: "created_at_asc", label: "Oldest created" },
    ],
    [],
  );

  // Save settings to local storage whenever visibleColumns changes
  useEffect(() => {
    localStorage.setItem(
      "projectListColumnVisibility",
      JSON.stringify(visibleColumns),
    );
  }, [visibleColumns]);

  // Listen for external reset event (e.g., from AddUpdateProjectModal)
  useEffect(() => {
    const handleReset = () => {
      // Only reset visual state — filter/URL state is handled by the parent's setFilters bulk update.
      // DO NOT call setQuarterFilter or any filter setter here, as it triggers a competing
      // setSearchParams call that overwrites the parent's bulk URL update.
      setResetKey((k) => k + 1); // Force remount selects to reflect defaults
      setLocalSearchInput(""); // Clear search text visually
    };

    window.addEventListener("reset-workunits-filters", handleReset);
    return () => {
      window.removeEventListener("reset-workunits-filters", handleReset);
    };
  }, [setQuarterFilter]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        columnDropdownRef.current &&
        !columnDropdownRef.current.contains(event.target as Node) &&
        dropdownContentRef.current &&
        !dropdownContentRef.current.contains(event.target as Node)
      ) {
        setShowColumnDropdown(false);
      }
    };

    if (showColumnDropdown) {
      updateDropdownPosition();
      window.addEventListener("scroll", updateDropdownPosition);
      window.addEventListener("resize", updateDropdownPosition);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      window.removeEventListener("scroll", updateDropdownPosition);
      window.removeEventListener("resize", updateDropdownPosition);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColumnDropdown]);

  return (
    <>
      {/* Filter Section - Clean design using app colors */}
      <div
        className={
          isCompact
            ? ""
            : "bg-secondary-light dark:bg-secondary-dark rounded-xl border border-light-border-default dark:border-gray-700/50 p-4 mb-2"
        }
        data-onboarding-id="project-filters"
      >
        {/* Row 1: Type Pills | Search | Source | Status | Stage | Columns | List/Grid */}
        <div className="flex items-center gap-3 animate-fade-in">
          {/* Type Pill Buttons - with animated indicator like TimelineNavBar */}

          <div className="relative inline-flex bg-white dark:bg-secondary-dark rounded-lg border border-gray-300 dark:border-gray-600 h-[42px] animate-fade-in">
            {/* Animated indicator */}

            <div
              className="absolute h-full bg-primary-light dark:bg-primary-dark rounded-lg transition-all duration-300 ease-in-out shadow-lg"
              style={{
                width: typeIndicatorStyle.width,
                left: typeIndicatorStyle.left,
                zIndex: 1,
              }}
            />

            {searchTypeOptions.map((option) => (
              <button
                key={option.value}
                ref={(el) => {
                  typeButtonRefs.current[option.value] = el;
                }}
                onClick={() =>
                  handleSearchTypeClick(
                    option.value as
                      | "all"
                      | "epic"
                      | "feature"
                      | "roadmap"
                      | "project",
                  )
                }
                className={`relative px-4 h-full text-sm font-medium transition-colors duration-300 rounded-lg ${
                  searchType === option.value
                    ? "text-white"
                    : "text-light-text-secondary dark:text-gray-400 hover:text-light-text-primary dark:hover:text-white"
                }`}
                style={{ zIndex: 2 }}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Search Input with Integrated Filters */}
          <div className="w-[380px] flex-shrink-0 ">
            <SearchWithFilter
              value={localSearchInput}
              onChange={(val) => handleSearch(val)}
              placeholder="Search work items..."
              filters={searchFilters}
              selectedFilters={{ quarter: quarterFilter || [] }}
              onFilterChange={handleFilterChange}
              width="w-full"
            />
          </div>

          {/* Status Dropdown */}
          <FilterSelect
            key={`status-${resetKey}`}
            options={statusFilterOptions}
            placeholder="All Statuses"
            onChange={handleStatusFilterChange}
            value={statusFilterOptions.filter((option: any) =>
              statusFilter.includes(option.value),
            )}
            className="w-[200px]"
            padding="py-2.5"
          />

          {/* Source Dropdown */}
          <div className="w-[140px]">
            <SingleSelect
              key={`source-${resetKey}`}
              name="sourceFilter"
              options={sourceFilterOptions}
              placeholder="All Sources"
              onChange={handleSourceFilterChange}
              value={sourceFilterOptions.find(
                (option: any) => option.value === sourceFilter,
              )}
              width="w-full"
            />
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* List/Grid Toggle */}
          <div className="flex items-center border border-light-border-default dark:border-gray-600 rounded-lg p-0.5 bg-tertiary-light dark:bg-tertiary-dark">
            <button
              onClick={() => setViewType("list")}
              className={`h-8 px-2.5 rounded-md transition-colors ${
                viewType === "list"
                  ? "bg-primary-light dark:bg-primary-dark text-white"
                  : "text-light-text-secondary dark:text-gray-400 hover:bg-hover-light dark:hover:bg-hover-dark"
              }`}
              title="List View"
            >
              <HiListBullet className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewType("grid")}
              className={`h-8 px-2.5 rounded-md transition-colors ${
                viewType === "grid"
                  ? "bg-primary-light dark:bg-primary-dark text-white"
                  : "text-light-text-secondary dark:text-gray-400 hover:bg-hover-light dark:hover:bg-hover-dark"
              }`}
              title="Grid View"
            >
              <HiOutlineViewGrid className="w-4 h-4" />
            </button>
          </div>

          {/* Pinned Filter */}
          <TooltipWrapper
            message={
              pinnedFilter ? "Show All Items" : "Show Only Pinned"
            }
          >
            <button
              onClick={handlePinnedFilterChange}
              className={`flex items-center gap-2 h-[35px] w-[35px] justify-center rounded-full border transition-all duration-200 border-gray-300 dark:border-gray-600 border-dashed ${
                pinnedFilter
                  ? "bg-primary-light/10 border-primary-light text-primary-light shadow-sm !border-solid"
                  : "bg-white dark:bg-secondary-dark border-gray-300 dark:border-gray-600 text-light-text-secondary dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500"
              }`}
            >
              <PinIcon
                className={`w-3.5   h-3.5 ${pinnedFilter ? "fill-current" : ""}`}
              />
            </button>
          </TooltipWrapper>
        </div>
      </div>

      {/* Separator line in compact mode */}
      {isCompact && (
        <div className="border-t border-light-border-subtle dark:border-gray-700/40 mt-3 pt-3" />
      )}

      {/* Row 2: Pagination Info | Sort (outside the box, shown in both modes) */}
      <div
        className={`flex items-center justify-between animate-fade-in ${isCompact ? "" : "mt-3"}`}
      >
        {/* Pagination Info */}
        {loading ? (
          <div className="flex gap-1.5 items-center">
            <div className="h-4 w-[52px] rounded bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
            <div className="h-4 w-[24px] rounded bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
            <div className="h-4 w-[14px] rounded bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
            <div className="h-4 w-[24px] rounded bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
            <div className="h-4 w-[68px] rounded bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
            <span className="text-light-text-muted dark:text-gray-500 mx-1">
              •
            </span>
            <div className="h-4 w-[80px] rounded bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
          </div>
        ) : (
          <div className="flex gap-1.5 items-center text-sm text-light-text-secondary dark:text-gray-400">
            {totalItems > 0 ? (
              <>
                <span>Showing</span>
                <span className="font-medium text-light-text-primary dark:text-gray-200 tabular-nums text-right min-w-[2ch] inline-block">
                  {getFilteredProjects().length}
                </span>
                <span>of</span>
                <span className="font-medium text-light-text-primary dark:text-gray-200 tabular-nums text-right min-w-[2ch] inline-block">
                  {totalItems}
                </span>
                <span>work items</span>
                {totalPages > 1 && (
                  <>
                    <span className="mx-1 text-light-text-muted dark:text-gray-500">
                      •
                    </span>
                    <span>
                      Page {currentPage} of {totalPages}
                    </span>
                  </>
                )}
                {loadingMore && (
                  <span
                    className="ml-1.5 inline-flex items-center"
                    title="Loading more items..."
                  >
                    <svg
                      className="animate-spin h-3.5 w-3.5 text-brand-blue"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </span>
                )}
              </>
            ) : (
              <span className="text-light-text-muted dark:text-gray-500">
                No work items
              </span>
            )}
          </div>
        )}

        {/* Sort By */}
        <div className="flex items-center gap-3">
          {hasActiveFilters && onClearFilters && (
            <button
              onClick={onClearFilters}
              className="text-sm text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium whitespace-nowrap transition-colors cursor-pointer hover:underline"
            >
              Clear Filters
            </button>
          )}
          <SortByDropdown value={sortBy} onChange={handleSortChange} />
        </div>
      </div>

      {/* Column Dropdown Portal */}
      {showColumnDropdown && (
        <Portal>
          <div
            ref={dropdownContentRef}
            className="fixed bg-secondary-light dark:bg-secondary-dark rounded-xl shadow-xl z-50 border border-light-border-default dark:border-gray-600 overflow-hidden"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: "200px",
            }}
          >
            <div className="px-3 py-2.5 border-b border-light-border-subtle dark:border-gray-700">
              <div className="text-xs font-medium text-light-text-tertiary dark:text-gray-400 uppercase tracking-wide">
                Columns
              </div>
            </div>
            <div className="p-2 overflow-y-auto" style={{ maxHeight: "320px" }}>
              {workUnitsTableHeaders
                .filter((c) => c.value !== "action")
                .filter((c) => {
                  if (c.value === "total_cost") {
                    return isAdmin && !!showCost;
                  }
                  return true;
                })
                .filter((c) => {
                  const isIntegrationColumn =
                    c.value === "integration_type" ||
                    c.value === "integration_status";
                  if (isIntegrationColumn) {
                    const azure = isIntegrationActive("azure");
                    const jira = isIntegrationActive("jira");
                    const asana = isIntegrationActive("asana");
                    const monday = isIntegrationActive("monday");
                    const anyActive = azure || jira || asana || monday;
                    return anyActive;
                  }
                  return true;
                })
                .map((col) => (
                  <div key={col.value} className="relative">
                    <label
                      htmlFor={`col-${col.value}`}
                      className="flex items-center gap-2.5 px-2 py-1.5 hover:bg-hover-light dark:hover:bg-hover-dark rounded-md cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        id={`col-${col.value}`}
                        checked={visibleColumns[col.value]}
                        onChange={() => handleColumnToggle(col.value)}
                        className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-primary-light dark:text-primary-dark focus:ring-primary-light/50 dark:focus:ring-primary-dark/50 bg-white dark:bg-gray-700"
                      />
                      <span className="text-sm text-light-text-primary dark:text-gray-300">
                        {col.label}
                      </span>
                    </label>
                    {showTooltip === col.value && (
                      <div className="absolute left-0 top-full mt-1 px-2.5 py-1.5 bg-secondary-light dark:bg-gray-800 text-light-text-primary dark:text-gray-200 text-xs rounded-md shadow-lg border border-light-border-default dark:border-gray-700 whitespace-nowrap z-50 flex items-center gap-1.5">
                        <svg
                          className="w-3 h-3 text-primary-light dark:text-primary-dark"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          ></path>
                        </svg>
                        <span>Keep at least one</span>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </Portal>
      )}
    </>
  );
};

export default WorkUnitsFilters;
